import React from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationBell.css';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  icon: string;
  data: any;
  read: boolean;
  created_at: string;
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Форматирование времени
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    const isRu = lang === 'ru';

    if (diff < 60) return isRu ? 'только что' : 'just now';
    if (diff < 3600) return isRu ? `${Math.floor(diff / 60)} мин назад` : `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return isRu ? `${Math.floor(diff / 3600)} ч назад` : `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return isRu ? `${Math.floor(diff / 86400)} дн назад` : `${Math.floor(diff / 86400)}d ago`;

    return date.toLocaleDateString(isRu ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Форматирование сообщения: преобразование \n в <br>
  const formatMessage = (message: string) => {
    if (!message) return '';

    // Преобразуем \n\n в разделители абзацев, а одиночные \n в <br>
    return message
      .replace(/\n\n+/g, '<div class="notification-divider"></div>')
      .replace(/\n/g, '<br>');
  };

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);
  };

  // Проверка на незаменённые переменные {{...}}
  const hasUninterpolatedVars = (str: string) => /\{\{.+?\}\}/.test(str);

  // Получаем переведённые title и message на основе типа уведомления
  const getTranslatedContent = () => {
    const typeKey = `notifications.types.${notification.type}`;

    // Подготавливаем данные для интерполяции
    let interpolationData = { ...(notification.data || {}) };

    // Для rank_upgrade: локализуем название ранга через rank_level
    if (notification.type === 'rank_upgrade' && notification.data?.rank_level !== undefined) {
      const localizedRankName = t(`ranks.${notification.data.rank_level}`, { defaultValue: '' }) as string;
      if (localizedRankName) {
        interpolationData.new_rank = localizedRankName;
      }
    }

    const translatedTitle = t(`${typeKey}.title`, { defaultValue: '' }) as string;
    const translatedMessage = t(`${typeKey}.message`, {
      defaultValue: '',
      ...interpolationData // Передаём данные для интерполяции
    }) as string;

    // Используем перевод только если он найден И все переменные заменены
    // Иначе используем сообщение из backend (оно уже отформатировано)
    const useTranslatedTitle = translatedTitle &&
      translatedTitle !== `${typeKey}.title` &&
      !hasUninterpolatedVars(translatedTitle);

    const useTranslatedMessage = translatedMessage &&
      translatedMessage !== `${typeKey}.message` &&
      !hasUninterpolatedVars(translatedMessage);

    return {
      title: useTranslatedTitle ? translatedTitle : notification.title,
      message: useTranslatedMessage ? translatedMessage : notification.message
    };
  };

  const { title: displayTitle, message: displayMessage } = getTranslatedContent();

  return (
    <div
      className={`notification-item ${notification.read ? 'read' : 'unread'}`}
      onClick={handleClick}
    >
      <div className="notification-icon">
        {notification.icon || '🔔'}
      </div>

      <div className="notification-content">
        <div className="notification-item-title">
          {displayTitle}
          {!notification.read && <span className="unread-dot">●</span>}
        </div>

        <div
          className="notification-item-message"
          dangerouslySetInnerHTML={{ __html: formatMessage(displayMessage || '') }}
        />

        <div className="notification-item-time">
          {formatTime(notification.created_at)}
        </div>
      </div>

      <div className="notification-actions">
        {!notification.read && (
          <button
            className="notification-action-btn mark-read"
            onClick={handleMarkAsRead}
            title={t('notifications.markAsRead')}
          >
            ✓
          </button>
        )}
        <button
          className="notification-action-btn delete"
          onClick={handleDelete}
          title={t('notifications.delete')}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;
